import { readFile, readdir, stat } from "node:fs/promises";
import type { RunnerMode, TaskContext } from "@project/shared";

interface ResolvedEntry {
  type: "rule" | "file" | "folder" | "doc";
  path: string;
  label?: string;
  content: string | null;
  charCount: number;
}

interface ResolvedTagContext {
  tagName: string;
  description: string | null;
  entries: ResolvedEntry[];
}

const TYPE_PRIORITY: Record<string, number> = { rule: 0, file: 1, folder: 2, doc: 3 };

// Binary file extensions to skip content injection
const BINARY_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".ico",
  ".svg",
  ".bmp",
  ".mp3",
  ".mp4",
  ".wav",
  ".avi",
  ".mov",
  ".pdf",
  ".zip",
  ".tar",
  ".gz",
  ".woff",
  ".woff2",
  ".ttf",
  ".eot",
  ".otf",
  ".exe",
  ".dll",
  ".so",
  ".dylib",
  ".wasm",
]);

function isBinaryPath(filePath: string): boolean {
  const ext = filePath.slice(filePath.lastIndexOf(".")).toLowerCase();
  return BINARY_EXTENSIONS.has(ext);
}

// Module-scope cache of rule/file contents keyed by absolute path.
// Entries are invalidated when mtime changes (post-merge rule updates)
// and can be cleared wholesale on session boundaries via clearTagContextCache().
const fileContentCache = new Map<string, { mtimeMs: number; content: string }>();
const folderListingCache = new Map<string, { mtimeMs: number; listing: string }>();
let fileReadCount = 0;
let folderReadCount = 0;

export function clearTagContextCache(): void {
  fileContentCache.clear();
  folderListingCache.clear();
}

export function _getTagContextCacheStatsForTest(): {
  files: number;
  folders: number;
  fileReads: number;
  folderReads: number;
} {
  return {
    files: fileContentCache.size,
    folders: folderListingCache.size,
    fileReads: fileReadCount,
    folderReads: folderReadCount,
  };
}

export function _resetTagContextReadCountsForTest(): void {
  fileReadCount = 0;
  folderReadCount = 0;
}

function getContextBudgetChars(_model: string, betas?: string[], runnerMode?: RunnerMode): number {
  const contextWindow = betas?.some((b) => b.includes("context-1m")) ? 1_000_000 : 200_000;
  // Task agents get 15% budget (plan already captures key context); PM/code-review get 25%
  const budgetPct = runnerMode === "task" ? 0.15 : 0.25;
  // ~4 chars per token
  return contextWindow * budgetPct * 4;
}

async function readFileContent(filePath: string, maxChars: number): Promise<string | null> {
  try {
    if (isBinaryPath(filePath)) return null;
    let rawContent: string;
    let mtimeMs: number;
    try {
      const st = await stat(filePath);
      mtimeMs = st.mtimeMs;
    } catch {
      return null;
    }
    const cached = fileContentCache.get(filePath);
    if (cached && cached.mtimeMs === mtimeMs) {
      rawContent = cached.content;
    } else {
      rawContent = await readFile(filePath, "utf-8");
      fileReadCount++;
      fileContentCache.set(filePath, { mtimeMs, content: rawContent });
    }
    if (rawContent.length > maxChars) {
      const omitted = rawContent.length - maxChars;
      return rawContent.slice(0, maxChars) + `\n[... truncated, ${omitted} chars omitted]`;
    }
    return rawContent;
  } catch {
    return null;
  }
}

async function readFolderListing(folderPath: string): Promise<string | null> {
  try {
    let mtimeMs: number;
    try {
      const st = await stat(folderPath);
      mtimeMs = st.mtimeMs;
    } catch {
      return null;
    }
    const cached = folderListingCache.get(folderPath);
    if (cached && cached.mtimeMs === mtimeMs) {
      return cached.listing;
    }
    const entries = await readdir(folderPath);
    folderReadCount++;
    const listing = `Files: ${entries.join(", ")}`;
    folderListingCache.set(folderPath, { mtimeMs, listing });
    return listing;
  } catch {
    return null;
  }
}

async function resolveEntry(
  entry: { type: string; path: string; label?: string },
  budget: { remaining: number },
): Promise<ResolvedEntry> {
  const result: ResolvedEntry = {
    type: entry.type as ResolvedEntry["type"],
    path: entry.path,
    label: entry.label,
    content: null,
    charCount: 0,
  };

  if (budget.remaining <= 0) return result;

  if (entry.type === "doc") {
    // doc type references project documents in DB — skip content injection
    return result;
  }

  if (entry.type === "folder") {
    const listing = await readFolderListing(entry.path);
    if (listing) {
      result.content = listing;
      result.charCount = listing.length;
      budget.remaining -= listing.length;
    }
    return result;
  }

  // rule or file — read from disk
  // Single file cap: 50% of remaining budget
  const maxChars = Math.floor(budget.remaining * 0.5) || budget.remaining;
  const content = await readFileContent(entry.path, maxChars);
  if (content) {
    result.content = content;
    result.charCount = content.length;
    budget.remaining -= content.length;
  }
  return result;
}

function formatEntry(entry: ResolvedEntry): string {
  const label = entry.label ? ` (${entry.label})` : "";
  const header = `#### ${entry.path}${label} (${entry.type})`;

  if (entry.content === null) {
    return `> ${entry.type}: ${entry.path}${label}`;
  }

  if (entry.type === "folder") {
    return `${header}\n${entry.content}`;
  }

  // rule or file — wrap in code fence
  return `${header}\n\`\`\`\n${entry.content}\n\`\`\``;
}

function formatResolvedTags(resolved: ResolvedTagContext[]): string {
  const parts: string[] = [`\n## Tag Context`];

  for (const tag of resolved) {
    if (tag.entries.length === 0) continue;
    const desc = tag.description ? ` — ${tag.description}` : "";
    parts.push(`\n### Tag: "${tag.tagName}"${desc}`);

    const contentEntries = tag.entries.filter((e) => e.content !== null);
    const pointerEntries = tag.entries.filter((e) => e.content === null);

    for (const entry of contentEntries) {
      parts.push(`\n${formatEntry(entry)}`);
    }

    if (pointerEntries.length > 0) {
      parts.push(``);
      parts.push(`> Budget limit reached. Remaining linked files (read manually if needed):`);
      for (const entry of pointerEntries) {
        parts.push(formatEntry(entry));
      }
    }
  }

  return parts.join("\n");
}

export async function resolveTagContext(
  projectTags: TaskContext["projectTags"],
  taskTagIds: string[],
  model: string,
  betas?: string[],
  runnerMode?: RunnerMode,
): Promise<{ injectedSection: string; stats: { injected: number; skipped: number } }> {
  if (!projectTags?.length || !taskTagIds.length) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  const taskTagIdSet = new Set(taskTagIds);
  const assignedTags = projectTags.filter((t) => taskTagIdSet.has(t.id));

  if (assignedTags.length === 0) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  // Collect all entries across tags, sorted by type priority
  const allEntries: { tagIndex: number; entry: { type: string; path: string; label?: string } }[] =
    [];
  for (let i = 0; i < assignedTags.length; i++) {
    const tag = assignedTags[i];
    if (!tag.contextPaths?.length) continue;
    const sorted = [...tag.contextPaths].sort(
      (a, b) => (TYPE_PRIORITY[a.type] ?? 99) - (TYPE_PRIORITY[b.type] ?? 99),
    );
    for (const entry of sorted) {
      allEntries.push({ tagIndex: i, entry });
    }
  }

  if (allEntries.length === 0) {
    return { injectedSection: "", stats: { injected: 0, skipped: 0 } };
  }

  const budgetChars = getContextBudgetChars(model, betas, runnerMode);
  const budget = { remaining: budgetChars };
  let injected = 0;
  let skipped = 0;

  // Resolve all entries
  const resolved: ResolvedTagContext[] = assignedTags.map((t) => ({
    tagName: t.name,
    description: t.description,
    entries: [],
  }));

  for (const { tagIndex, entry } of allEntries) {
    const result = await resolveEntry(entry, budget);
    resolved[tagIndex].entries.push(result);
    if (result.content === null) {
      skipped++;
    } else {
      injected++;
    }
  }

  return {
    injectedSection: formatResolvedTags(resolved),
    stats: { injected, skipped },
  };
}
